import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    IReclassificationAccountRef,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';

/**
 * The clickable cell in the "Umgliederung" column of the grouping table.
 * Renders the net reclassification sum for an account in a given period,
 * with an "add" icon on hover. Emits `cellClick` with the cell's context;
 * the parent component opens the modal.
 */
@Component({
    selector: 'aumico-reclassification-cell',
    templateUrl: './reclassification-cell.component.html',
    styleUrls: ['./reclassification-cell.component.scss'],
})
export class ReclassificationCellComponent {
    /** Net reclassification sum for this (account, period). Source = negative, target = positive. */
    @Input() value = 0;

    /** Composite ref identifying the cell's account. */
    @Input() accountRef!: IReclassificationAccountRef;

    /** Which period this cell represents (CY = closing, PY = opening). */
    @Input() period!: ReclassificationPeriod;

    /** Which statement the account belongs to. */
    @Input() statement!: ReclassificationStatement;

    /** Disable the cell (e.g. for non-editable rows like groups/sections). */
    @Input() disabled = false;

    /**
     * Row number of the account in the original financial data — passed through
     * from the account object so the parent can identify which specific instance
     * of a duplicate pair was clicked (without opening a disambiguation dialog).
     */
    @Input() rowNo: number | undefined = undefined;

    @Output() cellClick = new EventEmitter<{
        accountRef: IReclassificationAccountRef;
        period: ReclassificationPeriod;
        statement: ReclassificationStatement;
        /** Identifies the specific duplicate instance that was clicked. */
        rowNo: number | undefined;
    }>();

    get hasValue(): boolean {
        return Number.isFinite(this.value) && this.value !== 0;
    }

    get isNegative(): boolean {
        return this.hasValue && this.value < 0;
    }

    onClick(event: MouseEvent): void {
        event.stopPropagation();
        if (this.disabled) return;
        this.cellClick.emit({ accountRef: this.accountRef, period: this.period, statement: this.statement, rowNo: this.rowNo });
    }
}
